import { ensureJourneyDb, getById, listTable } from "../../db";
import { canAccessTransaction, listAccessibleTransactions, resolveActor } from "./access";
import { forbidden, notFound } from "./errors";

export const SYSTEM_AUDIT_ACTIONS = ["seed.demo", "schema.migrate"];

export const AUDIT_LABELS = {
  "inquiry.create": "Office inquiry",
  "thread.create": "Thread opened",
  "thread.archive": "Thread set aside",
  "export.generate": "Report saved",
  "transaction.archive": "File archived",
  "transaction.status_change": "Status changed",
  "file.status": "File status",
  "file.custom_step": "Custom step",
  "file.lifecycle": "File lifecycle",
  "file.seller_milestone": "Seller milestone",
  "file.profile_notes": "File notes",
  "file.attachment": "File attachment",
  "file.packet_export": "File packet PDF",
  "email.queue": "Report email queued",
  "email.opt_in": "Email preference",
  "seed.demo": "Demo seed",
  "schema.migrate": "Schema migrate",
  "beta.accept": "Beta accepted",
  "beta.feedback": "Beta feedback",
};

export function auditLabel(actionType) {
  return AUDIT_LABELS[actionType] || actionType;
}

export function auditDetail(row) {
  const meta = row.metadata || {};
  if (row.action_type === "transaction.status_change") {
    return [meta.from, meta.to].filter(Boolean).join(" → ");
  }
  if (row.action_type === "export.generate" && Array.isArray(meta.formats)) {
    return meta.formats.join(", ").toUpperCase();
  }
  if (row.action_type === "email.opt_in") {
    return meta.email_report_opt_in ? "Client asked for report copies" : "Client turned off report copies";
  }
  if (row.action_type === "email.queue") {
    const who = ["broker", "agent", "client"].filter((key) => meta[key]).join(", ");
    return who ? `Queued for ${who}` : meta.email || "";
  }
  if (meta.idempotent) return "Already recorded — no duplicate";
  if (typeof meta.email === "string") return meta.email;
  return "";
}

function hydrateAudit(row) {
  const actor = getById("users", row.user_id);
  const transaction = getById("transactions", row.transaction_id);
  return {
    ...row,
    actor_name: actor?.name || "",
    actor_role: actor?.role_type || "",
    property_address: transaction?.property_address || "",
    client_role: transaction?.client_role || "",
    agent_id: transaction?.agent_id || "",
    label: auditLabel(row.action_type),
    detail: auditDetail(row),
  };
}

/** GET /api/audit */
export function getAuditLogs(session, query = {}) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const accessible = listAccessibleTransactions(session);
  const allowedIds = new Set(accessible.map((item) => item.id));
  const transactionId = query.transaction_id || query.transactionId;
  if (transactionId) {
    const transaction = getById("transactions", transactionId);
    if (!transaction) throw notFound("Transaction not found");
    if (!canAccessTransaction(actor, transaction)) {
      throw forbidden("No access to this transaction log");
    }
  }

  const includeSystem = query.include_system === "1" || query.include_system === "true";
  let rows = listTable("audit_logs").filter((row) => {
    if (!includeSystem && SYSTEM_AUDIT_ACTIONS.includes(row.action_type)) return false;
    if (transactionId) return row.transaction_id === transactionId;
    if (!row.transaction_id) return actor.isSupervisor;
    return allowedIds.has(row.transaction_id);
  });

  return rows
    .sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || "")))
    .map(hydrateAudit);
}
